/**
 * `pointer-arrow`: two boxes and the arrow between them, the picture every
 * pointer lesson draws on a whiteboard.
 *
 * <div class="pc-widget" data-widget="pointer-arrow"
 *      data-boxes='[{"name":"x","value":"5","address":"0x1000"},
 *                   {"name":"p","value":"0x1000","address":"0x1008","pointsTo":"x"}]'
 *      data-caption="p holds the address of x, so *p reads 5.">
 *   <p class="pc-widget-fallback">p holds 0x1000, the address of x, and x holds 5.</p>
 * </div>
 *
 * The arrow is an inline SVG drawn over the boxes once they have been laid
 * out, and redrawn when the column width changes. The boxes themselves are the
 * first frame: they read correctly before the arrow arrives.
 */

import { buildShell, el, parseJson, type WidgetText } from "./dom";

interface Box {
  name: string;
  value: string;
  address?: string;
  pointsTo?: string;
}

const isBoxes = (value: unknown): value is Box[] =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every(
    (box) =>
      typeof box === "object" &&
      box !== null &&
      typeof (box as Box).name === "string" &&
      typeof (box as Box).value === "string",
  );

const SVG_NS = "http://www.w3.org/2000/svg";

export function renderPointerArrow(host: HTMLElement, text: WidgetText): boolean {
  const boxes = parseJson(host.dataset.boxes, isBoxes);
  if (!boxes) return false;

  const shell = buildShell(host, {
    label: text("labelPointer"),
    caption: host.dataset.caption,
  });

  const frame = el("div", "pc-pointer");
  const row = el("div", "pc-widget-cells");
  const nodes = new Map<string, HTMLElement>();
  for (const box of boxes) {
    const cell = el("div", "pc-cell");
    if (box.pointsTo) cell.classList.add("is-active");
    cell.append(el("span", "pc-cell-name", box.name));
    cell.append(el("span", "pc-cell-value", box.value));
    if (box.address) cell.append(el("span", "pc-cell-meta", box.address));
    row.append(cell);
    nodes.set(box.name, cell);
  }
  frame.append(row);

  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("class", "pc-pointer-arrows");
  svg.setAttribute("aria-hidden", "true");
  frame.append(svg);
  shell.body.append(frame);

  const draw = () => {
    while (svg.firstChild) svg.removeChild(svg.firstChild);
    const origin = frame.getBoundingClientRect();
    if (origin.width === 0) return;
    svg.setAttribute("viewBox", `0 0 ${origin.width} ${origin.height}`);
    for (const box of boxes) {
      if (!box.pointsTo) continue;
      const from = nodes.get(box.name)?.getBoundingClientRect();
      const to = nodes.get(box.pointsTo)?.getBoundingClientRect();
      if (!from || !to) continue;
      const x1 = from.left - origin.left + from.width / 2;
      const y1 = from.top - origin.top + from.height;
      const x2 = to.left - origin.left + to.width / 2;
      const y2 = to.top - origin.top + to.height;
      const drop = 22;
      const path = document.createElementNS(SVG_NS, "path");
      path.setAttribute(
        "d",
        `M ${x1} ${y1} C ${x1} ${y1 + drop}, ${x2} ${y2 + drop}, ${x2} ${y2 + 6}`,
      );
      path.setAttribute("fill", "none");
      path.setAttribute("stroke", "currentColor");
      path.setAttribute("stroke-width", "2");
      const head = document.createElementNS(SVG_NS, "polygon");
      head.setAttribute("points", `${x2},${y2} ${x2 - 5},${y2 + 9} ${x2 + 5},${y2 + 9}`);
      head.setAttribute("fill", "currentColor");
      svg.append(path, head);
    }
  };

  draw();
  if (typeof ResizeObserver !== "undefined") {
    const observer = new ResizeObserver(() => draw());
    observer.observe(frame);
  }
  return true;
}
